import type { Artifact } from "@/services/api/agentic-v2";
import type {
  AreaGrade,
  Assessment,
  CaseInconsistency,
  CompletenessCheck,
  RemedialAction,
} from "@/services/api/case-checking";
import type { RuleSetVersion } from "@/services/api/case-checking/rule-sets";

import {
  countRuleOutcomes,
  formatRuleAttentionLabel,
  getInScopeAssessmentRules,
} from "./assessmentRuleScope";
import { documentRequirementApplies } from "./documentRequirementsScope";
import { countInconsistencyRollups } from "./inconsistencyRollup";

export type CaseInsightNavTab =
  | "documents"
  | "completeness"
  | "inconsistencies"
  | "assessment"
  | "remedial";

export interface CaseDetailInsights {
  documentCount: number;
  missingDocuments: number | null;
  failedInconsistencies: number;
  unclearInconsistencies: number;
  failedRules: number;
  unclearRules: number;
  severeRemedialActions: number;
  openRemedialActions: number;
}

export function computeCaseDetailInsights(input: {
  documentCount: number;
  completeness: CompletenessCheck | null;
  assessment: Assessment | null;
  ruleSet?: RuleSetVersion | null;
  caseType?: string | null;
  caseTypes?: string[] | null;
  transactionSubtype?: string | null;
  inconsistencies: CaseInconsistency[];
  remedialActions: RemedialAction[];
}): CaseDetailInsights {
  const missingDocuments = countBlockingMissingDocuments(
    input.completeness,
    input.ruleSet,
    input.caseType,
    input.caseTypes,
    input.transactionSubtype,
  );

  const activeInconsistencies = input.inconsistencies.filter(
    (row) => row.status !== "pass" && row.check_id?.toUpperCase() !== "BACKSTOP",
  );
  const inconsistencyCounts = countInconsistencyRollups(activeInconsistencies);

  const inScopeRules = getInScopeAssessmentRules(
    input.assessment,
    input.ruleSet,
    input.caseType,
  );
  const { failed: failedRules, unclear: unclearRules } = countRuleOutcomes(inScopeRules);

  const openRemedials = input.remedialActions.filter(isOpenRemedialAction);
  const severeRemedialActions = openRemedials.filter((a) => a.priority === "high").length;

  return {
    documentCount: input.documentCount,
    missingDocuments,
    failedInconsistencies: inconsistencyCounts.fail,
    unclearInconsistencies: inconsistencyCounts.unclear,
    failedRules,
    unclearRules,
    severeRemedialActions,
    openRemedialActions: openRemedials.length,
  };
}

const GROUP_TO_AREA: Record<string, AreaGrade["area"]> = {
  kyc: "kyc",
  suitability: "suitability",
  "supporting-docs": "supporting-docs",
  disclosure: "supporting-docs",
};

export function isOpenRemedialAction(action: { status?: string | null }): boolean {
  return action.status !== "complete";
}

/**
 * Blocking = missing from the API AND marked `required` (universal / case-specific / subtype) in the
 * active rule set. Conditional slugs are never blocking, even if the API returns `required: true`.
 * If the rule set is not loaded we fall back to the API's own `.required` flag.
 */
export function countBlockingMissingDocuments(
  completeness: CompletenessCheck | null | undefined,
  ruleSet?: RuleSetVersion | null,
  caseType?: string | null,
  caseTypes?: string[] | null,
  transactionSubtype?: string | null,
): number | null {
  if (!completeness || completeness.status === "pending") return null;
  const missing = completeness.missing_documents;
  if (!ruleSet || !caseType) {
    return missing.filter((m) => m.required).length;
  }
  const requiredSlugs = new Set(
    (ruleSet.document_requirements ?? [])
      .filter((r) => r.presence === "required")
      .filter((r) => documentRequirementApplies(r, caseType, caseTypes, transactionSubtype))
      .map((r) => r.document_slug),
  );
  return missing.filter((m) => requiredSlugs.has(m.document_type)).length;
}

/** Prefer persisted API grades; otherwise derive from in-scope rules + inconsistencies. */
export function resolveAreaGrades(input: {
  persisted?: AreaGrade[] | null;
  review?: AreaGrade[] | null;
  assessment: Assessment | null | undefined;
  ruleSet: RuleSetVersion | null | undefined;
  caseType: string | null | undefined;
  inconsistencies: CaseInconsistency[];
}): AreaGrade[] {
  const fromApi = input.persisted?.length ? input.persisted : input.review;
  if (fromApi?.length) return fromApi;
  return computeLiveAreaGrades(
    input.assessment,
    input.ruleSet,
    input.caseType,
    input.inconsistencies,
  );
}

/** Live area pills from in-scope assessment rules + inconsistencies (matches API area_grade_rollup). */
export function computeLiveAreaGrades(
  assessment: Assessment | null | undefined,
  ruleSet: RuleSetVersion | null | undefined,
  caseType: string | null | undefined,
  inconsistencies: CaseInconsistency[],
): AreaGrade[] {
  const inScopeIds = new Set(
    getInScopeAssessmentRules(assessment, ruleSet, caseType).map((r) => r.rule_id),
  );
  const scopeRules = inScopeIds.size > 0;

  const driving: Record<AreaGrade["area"], string[]> = {
    kyc: [],
    suitability: [],
    "supporting-docs": [],
  };
  const failedAreas = new Set<AreaGrade["area"]>();
  const needsInfoAreas = new Set<AreaGrade["area"]>();

  for (const group of assessment?.groups ?? []) {
    const area = GROUP_TO_AREA[group.group_id];
    if (!area) continue;
    for (const rule of group.rules ?? []) {
      if (scopeRules && !inScopeIds.has(rule.rule_id)) continue;
      if (rule.status === "failed") {
        driving[area].push(rule.rule_id);
        failedAreas.add(area);
        needsInfoAreas.delete(area);
      } else if (rule.status === "unclear") {
        driving[area].push(rule.rule_id);
        if (!failedAreas.has(area)) needsInfoAreas.add(area);
      }
    }
  }

  for (const row of inconsistencies) {
    if ((row.grade_weight ?? "area_insufficient") !== "area_insufficient") continue;
    const status = (row.status ?? "").toLowerCase();
    if (status && !["fail", "flagged"].includes(status) && !["high", "medium"].includes(row.severity)) {
      continue;
    }
    const area = row.area as AreaGrade["area"] | undefined;
    if (!area || !driving[area]) continue;
    driving[area].push(row.check_id || row.field);
    failedAreas.add(area);
    needsInfoAreas.delete(area);
  }

  return (["kyc", "suitability", "supporting-docs"] as const).map((area) => ({
    area,
    status: failedAreas.has(area)
      ? "insufficient"
      : needsInfoAreas.has(area)
        ? "needs-info"
        : "sufficient",
    driving_check_ids: [...new Set(driving[area])],
  }));
}

export function pickReviewerNotesArtifact(artifacts: Artifact[]): Artifact | null {
  for (const artifact of artifacts) {
    const businessKind =
      (artifact.metadata as { business_kind?: string } | undefined)?.business_kind?.toLowerCase() ??
      "";
    if (businessKind.includes("reviewer")) return artifact;
    if (/reviewer[\s_-]*notes/i.test(artifact.filename ?? "")) return artifact;
  }
  return null;
}

export interface CaseInsightNavItem {
  tab: CaseInsightNavTab;
  label: string;
  detail: string;
  attention: boolean;
  /** When set, overrides default rose attention styling (e.g. unclear-only rules). */
  attentionTone?: "danger" | "warning";
}

export function buildCaseInsightNavItems(insights: CaseDetailInsights): CaseInsightNavItem[] {
  const checklistDetail =
    insights.missingDocuments === null
      ? "Not checked"
      : insights.missingDocuments === 0
        ? "Complete"
        : `${insights.missingDocuments} missing`;

  const inconsistencyDetail =
    insights.failedInconsistencies === 0
      ? insights.unclearInconsistencies === 0
        ? "None failed"
        : `${insights.unclearInconsistencies} unclear`
      : `${insights.failedInconsistencies} failed`;

  const rulesDetail =
    formatRuleAttentionLabel(insights.failedRules, insights.unclearRules) ?? "All passed";

  const actionsDetail =
    insights.severeRemedialActions === 0
      ? insights.openRemedialActions === 0
        ? "None open"
        : `${insights.openRemedialActions} open`
      : `${insights.severeRemedialActions} high priority`;

  const rulesNeedAttention = insights.failedRules > 0 || insights.unclearRules > 0;
  const rulesAttentionTone: "danger" | "warning" | undefined =
    insights.failedRules > 0 ? "danger" : insights.unclearRules > 0 ? "warning" : undefined;

  return [
    {
      tab: "documents",
      label: "Documents analysed",
      detail: String(insights.documentCount),
      attention: false,
    },
    {
      tab: "completeness",
      label: "Document checklist",
      detail: checklistDetail,
      attention: (insights.missingDocuments ?? 0) > 0,
    },
    {
      tab: "inconsistencies",
      label: "File inconsistencies",
      detail: inconsistencyDetail,
      attention: insights.failedInconsistencies > 0 || insights.unclearInconsistencies > 0,
      attentionTone: insights.failedInconsistencies > 0 ? "danger" : insights.unclearInconsistencies > 0 ? "warning" : undefined,
    },
    {
      tab: "assessment",
      label: "Assessment rules",
      detail: rulesDetail,
      attention: rulesNeedAttention,
      attentionTone: rulesAttentionTone,
    },
    {
      tab: "remedial",
      label: "Remedial actions",
      detail: actionsDetail,
      attention: insights.severeRemedialActions > 0,
    },
  ];
}
